// src/features/records/components/RecordDetail/hooks/useRecordAccess.js
import { useEffect, useMemo, useState } from 'react';
import { auth } from '../../../../../firebase';
import accessControlService, { ROLE_TO_LEVEL } from '../../../services/accessControlService';

/**
 * Hook for checking a user's access permissions on a record (new ACL system)
 *
 * Source of truth for permissions:
 * - Owner: record.access.owner
 * - Visibility: record.access.visibility ('private'|'friends'|'public')
 * - Effective role: /users/{userId}/accessibleRecords/{recordId}.permission (role, level)
 *
 * Notes:
 * - 'public' means viewable by any authenticated user even without a permission record
 * - 'friends' visibility expands to permission records via backend functions
 * - All database access goes through the service layer per app architecture
 *
 * @param {Object} record - Firestore record object with { id, access: { owner, visibility } }
 * @returns {{
 *   isLoading: boolean,
 *   error: string | null,
 *   canView: boolean,
 *   canEdit: boolean,
 *   canComment: boolean,
 *   canReact: boolean,
 *   canManageAccess: boolean,
 *   isOwner: boolean,
 *   accessLevel: 'none'|'viewer'|'editor'|'admin'|'owner',
 *   role: 'view'|'edit'|'admin'|null,
 *   roleLevel: number,               // 0..3 after owner override
 *   visibility: 'private'|'friends'|'public'|undefined,
 *   hasPermissionRecord: boolean,    // true iff user has explicit permission record
 *   sources: Record<string, number> | undefined, // provenance (when available)
 *   via: string[]                     // how access was granted ('owner'|'public'|grant keys)
 * }}
 */
export default function useRecordAccess(record) {
  const currentUser = auth.currentUser;
  const [edge, setEdge] = useState(null);   // user edge data from accessibleRecords
  const [edgeLoaded, setEdgeLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Subscribe to the user's permission record via service layer
  useEffect(() => {
    setError(null);
    setEdge(null);
    setEdgeLoaded(false);

    const uid = currentUser?.uid;
    const recordId = record?.id;

    if (!uid || !recordId) {
      // nothing to listen to
      setEdgeLoaded(true);
      return;
    }

    // Use service layer to subscribe to permission changes
    const unsubscribe = accessControlService.subscribeToUserPermission(
      recordId,
      uid,
      (edgeData) => {
        // Handle errors passed from service
        if (edgeData?.__error) {
          setError(edgeData.__error.message || 'Permission stream failed');
          setEdge(null);
        } else {
          setEdge(edgeData);
          setError(null);
        }
        setEdgeLoaded(true);
      }
    );

    return unsubscribe;
  }, [record?.id, currentUser?.uid]);

  return useMemo(() => {
    const visibility = record?.access?.visibility;
    const ownerId = record?.access?.owner;
    const uid = currentUser?.uid || null;

    const isOwner = Boolean(uid && ownerId && uid === ownerId);

    const edgePermission = edge?.permission;
    const edgeLifecycle = edge?.lifecycle ?? 'active';
    const isEdgeActive = edgeLifecycle !== 'deleted';
    const hasEdge = Boolean(edge) && isEdgeActive;
    const edgeRole = edgePermission?.role ?? null;
    const edgeRoleLevel = typeof edgePermission?.level === 'number'
      ? edgePermission.level
      : (edgeRole ? (ROLE_TO_LEVEL[edgeRole] ?? 0) : 0);
    const edgeSources = edgePermission?.sources ?? {};

    // Prefer server's level for forward compatibility, fallback to role string mapping
    const permissionRoleLevel = hasEdge ? edgeRoleLevel : 0;

    // Owner always has admin rights (level 3)
    const effectiveLevel = isOwner ? ROLE_TO_LEVEL.admin : permissionRoleLevel;

    // View logic (auth required for all modes per spec)
    let canView = false;
    if (uid) {
      if (isOwner) {
        canView = true;
      } else if (visibility === 'public') {
        // Public is viewable to any authenticated user even without a permission record
        canView = true;
      } else {
        // private/friends: rely on permission record produced by backend expansion
        canView = hasEdge && effectiveLevel >= ROLE_TO_LEVEL.view;
      }
    }

    const canEdit = effectiveLevel >= ROLE_TO_LEVEL.edit;           // edit or admin or owner
    const canManageAccess = isOwner || effectiveLevel >= ROLE_TO_LEVEL.admin; // admin-level
    const canComment = canView;                                      // policy: comment if you can view
    const canReact = canView;                                        // policy: react if you can view

    let accessLevel = 'none';
    if (isOwner) accessLevel = 'owner';
    else if (effectiveLevel >= ROLE_TO_LEVEL.admin) accessLevel = 'admin';
    else if (effectiveLevel >= ROLE_TO_LEVEL.edit) accessLevel = 'editor';
    else if (canView) accessLevel = 'viewer';

    // Determine access provenance for UI hints
    const via = isOwner
      ? ['owner']
      : visibility === 'public' && !hasEdge
        ? ['public']
        : Object.keys(edgeSources);

    // Optimize loading state - don't show spinner for immediate decisions
    const isLoading = !!uid && !!record?.id && 
                     !edgeLoaded && 
                     !isOwner && 
                     visibility !== 'public';

    return {
      isLoading,
      error,
      canView,
      canEdit,
      canComment,
      canReact,
      canManageAccess,
      isOwner,
      accessLevel,
      role: isOwner ? 'admin' : edgeRole,                // owner maps to admin semantically
      roleLevel: effectiveLevel,                         // 0..3 after owner override
      visibility,
      hasPermissionRecord: hasEdge,                      // explicit permission edge exists
      sources: edgeSources,                              // provenance map { 'user:uid'| 'group:gid' | 'public:*' : level }
      via                                                 // how access was granted
    };
  }, [record, currentUser, edge, edgeLoaded, error]);
}
